// src/hooks/useVapiCall.ts
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  VAPI_CONFIG,
  loadVapiSDK,
  type VapiInstance,
  type VapiCallSpecificConfig // Ensure this type is imported if used distinctly
} from '../pages/dashboard/hero-commands/configs';
import { supabase } from '../lib/supabaseClient';

export const fetchAssistantPrompt = async (assistantId: string): Promise<string> => {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke('get-vapi-assistant-prompt', {
      body: { assistantId },
    });

    if (invokeError) {
      console.error('Error invoking Supabase function get-vapi-assistant-prompt:', invokeError);
      let detailedMessage = invokeError.message;
      if (invokeError.context && invokeError.context.error && invokeError.context.error.message) {
        detailedMessage = invokeError.context.error.message;
      } else if (invokeError.context && invokeError.context.message) {
        detailedMessage = invokeError.context.message;
      }
      throw new Error(`Failed to fetch assistant configuration: ${detailedMessage}`);
    }

    if (data && data.error) {
      console.error('Supabase function returned an error:', data.error);
      throw new Error(`Failed to fetch assistant configuration: ${data.error}`);
    }

    if (!data || typeof data.prompt === 'undefined') {
      console.error('Invalid response structure from Supabase function get-vapi-assistant-prompt:', data);
      throw new Error('Failed to fetch assistant configuration: Invalid response from server');
    }

    return data.prompt;

  } catch (err) {
    console.error('Error in fetchAssistantPrompt:', err);
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error(String(err) || 'An unknown error occurred while fetching assistant prompt.');
    }
  }
};

export const useVapiCall = () => {
  const [isCalling, setIsCalling] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const vapiInstance = useRef<VapiInstance | null>(null);

  useEffect(() => {
    loadVapiSDK().catch(sdkLoadError => {
      console.error("Error loading VAPI SDK in useEffect:", sdkLoadError);
    });
    return () => {
      if (vapiInstance.current) {
        try { vapiInstance.current.stop(); } catch (e) { console.error('Error stopping VAPI call:', e); }
        vapiInstance.current = null;
      }
    };
  }, []);

  const checkMicrophonePermission = async (): Promise<boolean> => {
    try { await navigator.mediaDevices.getUserMedia({ audio: true }); return true; }
    catch (err) { toast.error('Microphone access is required for test calls'); return false; }
  };

  const setupVapiEvents = (instance: VapiInstance) => {
    instance.on('call-start', () => {
      console.log('Call started'); toast.success('Call started successfully');
      setIsCalling(true); setIsConnecting(false); setError(null);
    });
    instance.on('call-end', () => {
      console.log('Call ended'); toast.info('Call ended');
      setIsCalling(false); setIsConnecting(false);
      setError(null);
    });
    instance.on('error', (errorEvent: any) => {
      let errorMessage = 'Call error occurred';
      if (typeof errorEvent === 'object' && errorEvent !== null) {
        if (errorEvent?.error?.message && Array.isArray(errorEvent.error.message) && errorEvent.error.message.length > 0) {
          errorMessage = errorEvent.error.message.map((valError: any) => `${valError.field ? valError.field + ': ' : ''}${valError.message}`).join('; ');
        } else if (errorEvent.error && typeof errorEvent.error === 'object' && errorEvent.error.message) {
          errorMessage = Array.isArray(errorEvent.error.message) ? errorEvent.error.message.join(', ') : String(errorEvent.error.message);
        } else if (errorEvent.error && typeof errorEvent.error === 'string') {
          errorMessage = errorEvent.error;
        } else if (errorEvent.message && typeof errorEvent.message === 'string') {
          errorMessage = errorEvent.message;
        } else if (errorEvent.error?.error?.message) {
          errorMessage = String(errorEvent.error.error.message);
        } else { errorMessage = String(errorEvent.message || errorEvent.error || JSON.stringify(errorEvent)); }
      } else if (typeof errorEvent === 'string') { errorMessage = errorEvent; }
      console.error('VAPI call error event:', errorEvent); toast.error(errorMessage);
      setIsCalling(false); setIsConnecting(false); setError(errorMessage);
    });
    // instance.on('speech-start', () => console.log('VAPI Speech has started'));
    // instance.on('speech-end', () => console.log('VAPI Speech has ended'));
  };

  const initiateCall = async (assistantId: string, heroCommandContent: string, callContext: { selected_tool: string; custom_manual_instruction?: string; command_id: string; saas_call_type?: string; }, variables: Record<string, string>) => {
    try {
      if (!VAPI_CONFIG.apiKey || /^sk_/.test(String(VAPI_CONFIG.apiKey))) {
        const msg = 'Invalid Vapi API key for browser. Use your public key (pk_...). Set VITE_VAPI_API_KEY to the public key and restart.';
        toast.error(msg);
        throw new Error(msg);
      }
      if (vapiInstance.current) {
        vapiInstance.current.stop();
        vapiInstance.current = null;  
      }
      if (!assistantId) throw new Error('Assistant ID not configured. Please check your settings.');

      await loadVapiSDK();
      if (!window.Vapi && !window.vapiSDK) throw new Error('VAPI SDK not loaded correctly. Please try again.');

      const hasPermission = await checkMicrophonePermission();
      if (!hasPermission) {
        setIsConnecting(false);
        return;
      }

      setIsConnecting(true);
      setIsCalling(false);
      setError(null);

      const baseAssistantPrompt = await fetchAssistantPrompt(assistantId);

      const replacements = getCallReplacements(callContext);
      const systemPromptForCall = replaceTemplateVariables(baseAssistantPrompt, replacements);
      const commandPrompt = replaceTemplateVariables(heroCommandContent, variables);

      const finalPrompt = `${commandPrompt}, \n ${systemPromptForCall}`;

      if (window.Vapi) {
        vapiInstance.current = new window.Vapi(VAPI_CONFIG.apiKey);
      } else if (window.vapiSDK?.run) {
        vapiInstance.current = window.vapiSDK.run({ apiKey: VAPI_CONFIG.apiKey, assistant: assistantId });
      } else {
        throw new Error("VAPI SDK loaded but no known initialization method found.");
      }

      if (!vapiInstance.current) throw new Error("Failed to initialize Vapi instance.");

      setupVapiEvents(vapiInstance.current);

      const isInboundCall = callContext.selected_tool === 'inbound_call';
      const firstMessageMode: VapiCallSpecificConfig['firstMessageMode'] = isInboundCall
        ? undefined
        : "assistant-speaks-first-with-model-generated-message";

      const callConfig = {
        model: {
          provider: "openai",
          model: "gpt-5",
          messages: [{ role: "system", content: finalPrompt }]
        },
        metadata: {
          type: "web",
          test_call: true,
          origin: window.location.origin,
          command_id: callContext.command_id,
          saas_call_type: callContext.saas_call_type,
          selected_tool_for_preview: callContext.selected_tool,
        },
        ...(firstMessageMode && { firstMessageMode, firstMessage: "" }),
        variableValues: variables || {}
      };

      await vapiInstance.current.start(assistantId, callConfig);

    } catch (err) {
      console.error('Error initiating test call:', err);
      const specificErrorMessage = err instanceof Error ? err.message :
      (typeof err === 'object' && err !== null && 'message' in err) ? (err as { message: string }).message : typeof err === 'string' ? err : 'Failed to initiate test call';

      toast.error(specificErrorMessage);
      setError(specificErrorMessage);
      setIsCalling(false);
      setIsConnecting(false);
    }
  };

  const endCall = () => {
    if (vapiInstance.current) {
      try { vapiInstance.current.stop(); } catch (e) { console.error('Error stopping VAPI call during endCall:', e); }
      vapiInstance.current = null;
    }
    setIsCalling(false); setIsConnecting(false);
    setError(null);
  };

  return { isCalling, isConnecting, error, initiateCall, endCall, setError };
};

// const mergeHeroCommand = (existingPrompt: string, heroCommand: string): string => {
//   const priorityHeaderRegex = /^### 14\.2\. --- User's Hero Commands --- \(HIGHEST PRIORITY\)$/m;

//   if (priorityHeaderRegex.test(existingPrompt)) {
//     return existingPrompt.replace(
//       priorityHeaderRegex,
//       `### 14.2. --- User's Hero Commands --- (HIGHEST PRIORITY)\n${heroCommand}`
//     );
//   } else {
//     const priorityBlock = `### 14.2. --- User's Hero Commands --- (HIGHEST PRIORITY)\n${heroCommand}\n\n`;
//     return `${priorityBlock}${existingPrompt}`;
//   }
// };

// Helper function for template replacement

const replaceTemplateVariables = (template: string, replacements: Record<string, string>): string => {
  return Object.entries(replacements).reduce((result, [key, value]) => {
    // Handle both formats: with and without {{}} brackets
    const placeholder = key.startsWith('{{') && key.endsWith('}}') ? key : `{{${key}}}`;
    return result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
  }, template);
};

// Helper function to get call replacements based on context
const getCallReplacements = (callContext: { selected_tool: string; custom_manual_instruction?: string; }): Record<string, string> => {
  const { selected_tool } = callContext;
  console.log(selected_tool, "SELECTED TOOL");

  if (selected_tool === 'manual_upload') {
    const instruction = callContext.custom_manual_instruction || "No specific instruction provided for this manual upload test.";
    return {
      '{{call_objective}}': 'manual_upload',
      '{{specific_instruction_for_manual_upload}}': instruction,
    };
  }

  const toolToObjectiveMap: Record<string, string> = {
    'appointment_confirmation': 'confirm_appointment',
    'recover_cancelled': 'recover_cancellation',
    'recover_no_show': 'reschedule_noshow',
    'inbound_call': '',
  };

  const callObjectiveValue = toolToObjectiveMap[selected_tool] || selected_tool;

  return {
    '{{call_objective}}': callObjectiveValue,
    '{{specific_instruction_for_manual_upload}}': '',
  };
};